import { createReducer, on } from '@ngrx/store';
import {
  LoadProject,
  LoadProjectSummaries,
  LoadProjectSummary,
  ProjectCreated,
  ProjectUpdated,
  ReceiveProjectSummaries,
  ReceiveProjectSummary,
  RemoveProjectsApiError,
  SetCurrentProject,
  SetLoadProjectError,
  SetProjectsApiError,
  SetProjectSortOrder,
  UpdateProject,
} from './project.actions';
import { ProjectApiError } from './project.errors';
import { Project } from './models/project.model';
import { ProjectSummary } from './models/project-summary.model';
import {
  DeployMediaAssetCreatorConfigStarted,
  DeployPreviewConfigStarted,
} from '../config/config.actions';
import {
  ProjectEditFormExitedAction,
  ProjectEditFormLoadedAction,
} from '../project-edit-form/project-edit-form.actions';

export interface ProjectQuery {
  showAll?: boolean;
}

export enum ProjectSortOrderType {
  DateAsc = 'configDatetimeAdded ASC',
  DateDesc = 'configDatetimeAdded DESC',
  NameAsc = 'name ASC',
  NameDesc = 'name DESC',
}

export interface ProjectState {
  current: Project;
  projects: Project[];
  projectSummary: ProjectSummary;
  projectSummaries: ProjectSummary[];
  apiError: ProjectApiError;
  sortOrder: ProjectSortOrderType;
  query: ProjectQuery;
  requestInProgress: boolean;
  requestProjectSummaryInProgress: boolean;
  previewProjectId: string | null; // for project currently being edited
}

export const initialState: ProjectState = {
  current: null,
  projects: null,
  projectSummary: null,
  projectSummaries: null,
  apiError: null,
  sortOrder: ProjectSortOrderType.DateDesc,
  requestInProgress: false,
  requestProjectSummaryInProgress: false,
  query: { showAll: false },
  previewProjectId: null,
};

export const projectReducer = createReducer(
  initialState,
  on(UpdateProject, (state) => ({
    ...state,
    requestInProgress: true,
    current: null,
  })),
  on(LoadProject, (state) => ({
    ...state,
    requestInProgress: true,
    apiError: null,
    current: null,
  })),
  on(SetCurrentProject, (state, { project }) => ({
    ...state,
    current: project,
    requestInProgress: false,
  })),
  on(ProjectEditFormLoadedAction, (state, { project }) => ({
    ...state,
    previewProjectId: project.previewProjectId ?? null,
  })),
  on(DeployPreviewConfigStarted, DeployMediaAssetCreatorConfigStarted, (state, { task }) => ({
    ...state,
    previewProjectId: task.previewProjectId,
  })),
  on(ProjectEditFormExitedAction, (state) => ({
    ...state,
    previewProjectId: null,
  })),
  on(ProjectCreated, (state, { project }) => ({
    ...state,
    requestInProgress: false,
    current: project,
  })),
  on(ProjectUpdated, (state, { projectSettings }) => ({
    ...state,
    requestInProgress: false,
    current: { ...state.current, ...projectSettings },
  })),
  on(LoadProjectSummary, (state) => ({
    ...state,
    projectSummary: null,
    projectSummaries: null,
    requestInProgress: true,
    requestProjectSummaryInProgress: true,
  })),
  on(SetLoadProjectError, (state, { error }) => ({
    ...state,
    requestInProgress: false,
    apiError: error,
  })),
  on(SetProjectsApiError, (state, { error }) => ({
    ...state,
    requestInProgress: false,
    requestProjectSummaryInProgress: false,
    apiError: error,
  })),
  on(RemoveProjectsApiError, (state) => ({ ...state, apiError: null })),
  on(SetProjectSortOrder, (state, { sortOrder }) => ({
    ...state,
    sortOrder,
    requestInProgress: true,
  })),
  on(LoadProjectSummaries, (state, { query }) => ({
    ...state,
    requestInProgress: true,
    projectSummaries: null,
    projectSummary: null,
    query: query || state.query,
  })),
  on(ReceiveProjectSummaries, (state, { payload }) => ({
    ...state,
    requestInProgress: false,
    projectSummaries: payload,
  })),
  on(ReceiveProjectSummary, (state, { payload }) => ({
    ...state,
    requestInProgress: false,
    requestProjectSummaryInProgress: false,
    projectSummary: payload,
  })),
);
